import { useEffect, useState } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';

interface AuthCallbackProps {
  /**
   * A dónde redirigir tras login exitoso. Puede ser:
   *   - string: URL fija (ej. '/' para que cargue lo de la app actual).
   *   - función: recibe el rol_app del JWT y devuelve URL (ej. para mandar
   *     cliente_elawan a dashboard y staff a equipo).
   */
  redirectTo?: string | ((rolApp: string | null) => string);
  /** Componente mientras procesa el callback. */
  loading?: React.ReactNode;
}

/**
 * Página `/auth/callback` que recoge:
 *   - PKCE  : `?code=...` en query → exchangeCodeForSession
 *   - Implicit: `#access_token=...&refresh_token=...` en hash → setSession
 *
 * Robusto: parsea el hash manualmente porque `detectSessionInUrl` de
 * supabase-js a veces falla en Safari (cross-origin localStorage, hash
 * stripping, race condition con StrictMode double-mount).
 *
 * Si hay error, lo muestra con UI visible. Estados de loading siempre
 * pintan algo visible (no blank-screen).
 */
export function AuthCallback({ redirectTo = '/', loading }: AuthCallbackProps) {
  const [status, setStatus] = useState<'processing' | 'ok' | 'error'>('processing');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setStatus('error');
      setErrMsg('Supabase no configurado.');
      return;
    }
    const sb = getSupabase();
    let cancel = false;

    async function processCallback(): Promise<void> {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');

        // Sesión que vamos a usar para el redirect. La preferimos directa de
        // exchangeCodeForSession/setSession (que ya la devuelven) en lugar
        // de re-llamar getSession() — eso último puede devolver null por
        // race condition con el Web Lock interno de supabase-auth-js cuando
        // useSession.ensureInitialized tomó el lock primero al montar el
        // módulo. setSession ya escribió a localStorage antes de devolver.
        let session: import('@supabase/supabase-js').Session | null = null;

        // ── Camino PKCE ──────────────────────────────────────────────
        if (code) {
          const { data: exchData, error: exchErr } = await sb.auth.exchangeCodeForSession(code);
          if (exchErr) throw exchErr;
          session = exchData.session;
        }

        // ── Camino Implicit: hash con access_token / refresh_token ──
        // Parseamos manualmente porque detectSessionInUrl de supabase-js
        // a veces no procesa el hash en Safari (timing, cross-origin store).
        const hash = window.location.hash?.startsWith('#')
          ? window.location.hash.slice(1)
          : window.location.hash ?? '';
        if (hash.includes('access_token=')) {
          const params = new URLSearchParams(hash);
          const access_token = params.get('access_token');
          const refresh_token = params.get('refresh_token');
          if (access_token && refresh_token) {
            const { data: setData, error: setErr } = await sb.auth.setSession({ access_token, refresh_token });
            if (setErr) throw setErr;
            session = setData.session;
          }
        }

        // Fallback: si por algún motivo no tenemos session todavía, intenta
        // getSession (el helper que vivió aquí siempre). Si tampoco hay,
        // recién ahí escalamos como error real.
        if (!session) {
          const { data, error } = await sb.auth.getSession();
          if (error) throw error;
          session = data.session;
        }

        if (cancel) return;
        if (!session) {
          throw new Error('No se pudo establecer la sesión. El enlace pudo expirar.');
        }

        // Decode JWT para leer rol_app (custom hook lo inyecta en app_metadata
        // del access_token, NO en raw_app_meta_data; user.app_metadata puede
        // estar vacío). El destino dinámico necesita el rol.
        const rolApp = readRolFromJwt(session.access_token);

        if (!cancel) {
          setStatus('ok');
          // ?next= override: si el caller pasó un destino específico (ej.
          // "Vista cliente" → "/?preview=cliente"), respetarlo siempre
          // que pase el sanitize whitelist. Si no, usar redirectTo prop.
          const nextParam = url.searchParams.get('next');
          const destRaw = nextParam
            ? nextParam
            : (typeof redirectTo === 'function' ? redirectTo(rolApp) : redirectTo);
          // Defensa contra Open Redirect: whitelist (*.aosenumareport.com
          // + localhost dev). Lo que no pasa cae a "/".
          const destSafe = sanitizeRedirect(destRaw);
          if (typeof window !== 'undefined') {
            const dest = appendTokensIfCrossOrigin(destSafe, session);
            window.history.replaceState(null, '', window.location.pathname);
            setTimeout(() => window.location.replace(dest), 50);
          }
        }
      } catch (e) {
        if (cancel) return;
        // Log al console para que el dev pueda ver el error completo en
        // las DevTools del browser (mensaje + stack). Sentry también lo
        // captura si está activo.
        // eslint-disable-next-line no-console
        console.error('[AuthCallback] processCallback failed:', e);
        setStatus('error');
        setErrMsg(e instanceof Error ? e.message : String(e));
      }
    }

    void processCallback();

    return () => {
      cancel = true;
    };
  }, [redirectTo]);

  if (status === 'processing') {
    return loading ?? <DefaultLoading text="Iniciando sesión…" />;
  }
  if (status === 'error') {
    return (
      <div style={shellStyle()}>
        <div style={cardStyle()}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>No pudimos iniciar sesión</h2>
          <p style={{ margin: '12px 0', color: '#dc2626', fontSize: 13, lineHeight: 1.5 }}>
            {errMsg ?? 'Error desconocido.'}
          </p>
          <p style={{ margin: 0, fontSize: 13 }}>
            <a href="/" style={{ color: 'var(--teal-bright, #66b8c0)', textDecoration: 'underline' }}>
              Volver al inicio
            </a>
          </p>
        </div>
      </div>
    );
  }
  // status === 'ok' — pintamos algo visible mientras dispara el redirect.
  return <DefaultLoading text="Sesión verificada · redirigiendo…" />;
}

function DefaultLoading({ text }: { text: string }) {
  return (
    <div style={shellStyle()}>
      <div style={cardStyle()}>
        <div style={spinnerStyle()} />
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-mid, #555)' }}>{text}</p>
      </div>
    </div>
  );
}

/**
 * Whitelist de hosts a los que se permite redirigir post-login.
 *
 * Cualquier otra URL (atacante manipula JWT/redirectTo, deep link
 * envenenado) cae al pathname actual. Defensa contra Open Redirect.
 *
 * Reglas:
 *   - paths relativos ("/", "/dashboard"): siempre OK
 *   - mismo origen que la página: siempre OK
 *   - *.aosenumareport.com en https: OK (los 3 SPAs prod)
 *   - localhost:5173|5174|5175 en http: OK (dev)
 *   - todo lo demás: rechazar → fallback "/"
 */
function sanitizeRedirect(dest: string): string {
  if (typeof window === 'undefined') return dest;
  // Path relativo o protocolo-relativo: siempre seguro.
  if (dest.startsWith('/') && !dest.startsWith('//')) return dest;
  let parsed: URL;
  try {
    parsed = new URL(dest, window.location.origin);
  } catch {
    return '/';
  }
  // javascript:, data:, file: y similares.
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '/';
  if (parsed.origin === window.location.origin) return parsed.toString();
  const host = parsed.hostname.toLowerCase();
  // Hosts de produccion: *.aosenumareport.com bajo HTTPS.
  if (parsed.protocol === 'https:' && (host === 'aosenumareport.com' || host.endsWith('.aosenumareport.com'))) {
    return parsed.toString();
  }
  // Dev: localhost en los 3 puertos conocidos.
  if (
    parsed.protocol === 'http:' &&
    (host === 'localhost' || host === '127.0.0.1') &&
    ['5173', '5174', '5175'].includes(parsed.port)
  ) {
    return parsed.toString();
  }
  // eslint-disable-next-line no-console
  console.warn('[AuthCallback] redirect bloqueado por whitelist:', dest);
  return '/';
}

/**
 * Si `dest` apunta a otro origin (típico: callback procesado en landing
 * pero destino es dashboard/equipo en otro puerto/subdominio), anexa los
 * tokens en el hash para que el AuthCallback del destino pueda hacer
 * setSession en SU localStorage. Mismo origin: no toca.
 *
 * Seguridad: el access_token ya viajó en el URL del magic link inicial;
 * solo lo retransmitimos al subdominio hermano. El hash NO se envía al
 * server (no aparece en logs HTTP). Una vez setSession, el hash se limpia.
 */
function appendTokensIfCrossOrigin(
  dest: string,
  session: { access_token: string; refresh_token: string },
): string {
  if (typeof window === 'undefined') return dest;
  if (dest.startsWith('/') && !dest.startsWith('//')) return dest;
  let parsed: URL;
  try {
    parsed = new URL(dest, window.location.origin);
  } catch {
    return dest;
  }
  if (parsed.origin === window.location.origin) return parsed.toString();
  // Cross-origin: anexar tokens en hash. AuthCallback los detectará por
  // la presencia de `access_token=` y hará setSession en el otro origin.
  // Aterrizamos en /auth/callback explícito para que el AuthCallback corra.
  if (!parsed.pathname.endsWith('/auth/callback')) {
    parsed.pathname = parsed.pathname.replace(/\/$/, '') + '/auth/callback';
  }
  const hashParams = new URLSearchParams({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    type: 'bearer',
  });
  parsed.hash = '#' + hashParams.toString();
  return parsed.toString();
}

/** Decodifica `payload.app_metadata.rol_app` del JWT (sin verificar firma). */
function readRolFromJwt(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { app_metadata?: { rol_app?: string } };
    return payload.app_metadata?.rol_app ?? null;
  } catch {
    return null;
  }
}

function shellStyle(): React.CSSProperties {
  return {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: 'var(--bg-deep, #0c1216)',
    color: 'var(--text, #f2f4ed)',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  };
}

function cardStyle(): React.CSSProperties {
  return {
    background: 'var(--bg-glass, rgba(15,22,27,0.72))',
    border: '1px solid var(--glass-border, rgba(255,255,255,0.08))',
    borderRadius: 12,
    padding: '28px 32px',
    maxWidth: 420,
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
  };
}

function spinnerStyle(): React.CSSProperties {
  return {
    width: 28,
    height: 28,
    margin: '0 auto 16px',
    border: '2px solid var(--glass-border, rgba(255,255,255,0.12))',
    borderTopColor: 'var(--btn-primary-bg, #b4c96f)',
    borderRadius: '50%',
    animation: 'aosenuma-spin 0.8s linear infinite',
  };
}

// Inyectamos los keyframes una vez al cargar el módulo (idempotente).
if (typeof document !== 'undefined' && !document.getElementById('aosenuma-spin-kf')) {
  const style = document.createElement('style');
  style.id = 'aosenuma-spin-kf';
  style.textContent = '@keyframes aosenuma-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}
